import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Plus, CheckCircle, XCircle } from "lucide-react"
import { formatBRDate, todayISODate } from "@/lib/br-dates"
import { formatBRL } from "@/lib/money"
import { API_BASE } from '@/lib/api-base'

interface ContaReceber {
  id: string
  pedido_id: string | null
  pedido_numero: string | null
  cliente_id: string | null
  cliente_nome: string | null
  descricao: string | null
  valor_original: number
  valor_aberto: number
  data_vencimento: string
  status: string
  recebido_em: string | null
  meio_recebimento: string | null
  observacao: string | null
  criado_em: string
  dias_atraso: number | null
}

interface Resumo {
  total_pendente: number
  total_atrasado: number
  total_recebido_periodo: number
  quantidade_pendente: number
  quantidade_atrasado: number
  quantidade_recebido: number
}

interface Cliente {
  id: string
  nome: string
  status: string
}

const STATUS_LABELS: Record<string, string> = {
  PENDENTE: "Pendente",
  RECEBIDO: "Recebido",
  ATRASADO: "Atrasado",
  CANCELADO: "Cancelado",
}

const STATUS_COLORS: Record<string, "secondary" | "default" | "destructive" | "outline"> = {
  PENDENTE: "secondary",
  RECEBIDO: "default",
  ATRASADO: "destructive",
  CANCELADO: "outline",
}

const MEIOS_RECEBIMENTO = [
  { value: "PIX", label: "PIX" },
  { value: "DINHEIRO", label: "Dinheiro" },
  { value: "BOLETO", label: "Boleto" },
  { value: "CARTAO", label: "Cartao" },
  { value: "TRANSFERENCIA", label: "Transferencia" },
  { value: "OUTRO", label: "Outro" },
]

function ContasReceberPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [contas, setContas] = useState<ContaReceber[]>([])
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [showForm, setShowForm] = useState(false)
  const [showReceber, setShowReceber] = useState<ContaReceber | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>("")
  const [formReceber, setFormReceber] = useState({ meio_recebimento: "PIX", observacao: "" })
  const [form, setForm] = useState({
    cliente_id: "",
    descricao: "",
    valor_original: 0,
    data_vencimento: todayISODate(),
    observacao: "",
  })

  const fetchContas = useCallback(async () => {
    try {
      const url = filterStatus
        ? `${apiBase}/api/contas-receber/?status=${filterStatus}`
        : `${apiBase}/api/contas-receber/`
      const res = await apiFetch(url)
      if (res.ok) {
        setContas(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase, filterStatus])

  const fetchResumo = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/contas-receber/resumo`)
      if (res.ok) {
        setResumo(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  const fetchClientes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/clientes/`)
      if (res.ok) {
        setClientes((await res.json()).filter((c: Cliente) => c.status === "ATIVO"))
      }
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchContas()
    void fetchResumo()
    void fetchClientes()
  }, [user, isSuperAdmin, selectedTenantId, fetchContas, fetchResumo, fetchClientes])

  useEffect(() => {
    if (user && (!isSuperAdmin || selectedTenantId)) {
      void fetchContas()
    }
  }, [user, isSuperAdmin, selectedTenantId, filterStatus, fetchContas])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.valor_original || form.valor_original <= 0) {
      setSubmitError("Valor deve ser maior que zero.")
      return
    }
    setSubmitError(null)
    const payload = {
      cliente_id: form.cliente_id || null,
      descricao: form.descricao.trim() || null,
      valor_original: Number(form.valor_original),
      data_vencimento: form.data_vencimento,
      observacao: form.observacao.trim() || null,
    }
    try {
      const res = await apiFetch(`${apiBase}/api/contas-receber/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setForm({
          cliente_id: "",
          descricao: "",
          valor_original: 0,
          data_vencimento: todayISODate(),
          observacao: "",
        })
        fetchContas()
        fetchResumo()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleReceber = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!showReceber) return
    setSubmitError(null)
    try {
      const res = await apiFetch(`${apiBase}/api/contas-receber/${showReceber.id}/receber`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meio_recebimento: formReceber.meio_recebimento,
          observacao: formReceber.observacao.trim() || null,
        }),
      })
      if (res.ok) {
        setShowReceber(null)
        setFormReceber({ meio_recebimento: "PIX", observacao: "" })
        fetchContas()
        fetchResumo()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao receber.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleCancelar = async (conta: ContaReceber) => {
    if (!confirm(`Cancelar titulo ${conta.pedido_numero || conta.descricao || conta.id}?`)) return
    try {
      const res = await apiFetch(`${apiBase}/api/contas-receber/${conta.id}/cancelar`, {
        method: "POST",
      })
      if (res.ok) {
        fetchContas()
        fetchResumo()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        alert(errData?.detail || "Erro ao cancelar.")
      }
    } catch (e) {
      console.error(e)
      alert("Erro de conexao.")
    }
  }

  const formatCurrency = (value: unknown) => formatBRL(value)

  const isAtrasado = (conta: ContaReceber) => {
    return conta.status === "PENDENTE" && conta.dias_atraso && conta.dias_atraso > 0
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Contas a Receber</h1>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
        }}>
          <Plus className="h-4 w-4 mr-2" /> Novo Titulo
        </Button>
      </div>

      {resumo && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendente</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(resumo.total_pendente)}</p>
              <p className="text-xs text-muted-foreground">{resumo.quantidade_pendente} titulos</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">Atrasado</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">{formatCurrency(resumo.total_atrasado)}</p>
              <p className="text-xs text-red-600 dark:text-red-400">{resumo.quantidade_atrasado} titulos</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Recebido (periodo)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{formatCurrency(resumo.total_recebido_periodo)}</p>
              <p className="text-xs text-green-600 dark:text-green-400">{resumo.quantidade_recebido} titulos</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex gap-2">
        <select
          className="text-sm border rounded px-2 py-1 bg-background"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="PENDENTE">Pendente</option>
          <option value="RECEBIDO">Recebido</option>
          <option value="CANCELADO">Cancelado</option>
        </select>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Novo Titulo a Receber</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Novo Titulo a Receber</h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={form.cliente_id}
                  onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Descricao</Label>
                <Input
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  placeholder="Ex: Venda de ovos"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor *</Label>
                  <Input
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={form.valor_original || ""}
                    onChange={(e) => setForm({ ...form, valor_original: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vencimento *</Label>
                  <Input
                    type="date"
                    value={form.data_vencimento}
                    onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Observacao</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.observacao}
                  onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!showReceber}
        onOpenChange={(open) => {
          if (!open) {
            setShowReceber(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Receber Titulo</DialogTitle>
          {showReceber && (
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold pr-10">Receber Titulo</h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <div className="p-3 rounded bg-muted">
              <p><strong>Cliente:</strong> {showReceber.cliente_nome || "N/A"}</p>
              <p><strong>Valor:</strong> {formatCurrency(showReceber.valor_aberto)}</p>
              <p><strong>Vencimento:</strong> {formatBRDate(showReceber.data_vencimento)}</p>
            </div>
            <form onSubmit={handleReceber} className="space-y-4">
              <div className="space-y-2">
                <Label>Meio de Recebimento *</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={formReceber.meio_recebimento}
                  onChange={(e) => setFormReceber({ ...formReceber, meio_recebimento: e.target.value })}
                  required
                >
                  {MEIOS_RECEBIMENTO.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Observacao</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formReceber.observacao}
                  onChange={(e) => setFormReceber({ ...formReceber, observacao: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit"><CheckCircle className="h-4 w-4 mr-2" />Confirmar Recebimento</Button>
                <Button variant="outline" type="button" onClick={() => { setShowReceber(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {contas.map((c) => (
          <Card key={c.id} className={isAtrasado(c) ? "border-red-200" : ""}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {c.pedido_numero && <span className="font-mono font-bold">{c.pedido_numero}</span>}
                    <Badge variant={isAtrasado(c) ? "destructive" : STATUS_COLORS[c.status] || "secondary"}>
                      {isAtrasado(c) ? `Atrasado (${c.dias_atraso} dias)` : STATUS_LABELS[c.status] || c.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Cliente: {c.cliente_nome || "Nao informado"}
                  </p>
                  {c.descricao && (
                    <p className="text-sm text-muted-foreground">{c.descricao}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Vencimento: {formatBRDate(c.data_vencimento)}
                  </p>
                  {c.recebido_em && (
                    <p className="text-sm text-muted-foreground">
                      Recebido em: {formatBRDate(c.recebido_em)} via {c.meio_recebimento}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">{formatCurrency(c.valor_aberto)}</p>
                  {c.valor_aberto !== c.valor_original && (
                    <p className="text-xs text-muted-foreground line-through">{formatCurrency(c.valor_original)}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 flex gap-2">
                {(c.status === "PENDENTE" || c.status === "ATRASADO") && (
                  <>
                    <Button size="sm" onClick={() => {
                      setSubmitError(null)
                      setShowReceber(c)
                      setFormReceber({ meio_recebimento: "PIX", observacao: "" })
                    }}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Receber
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleCancelar(c)}>
                      <XCircle className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {contas.length === 0 && (
        <div className="py-8 text-center text-muted-foreground">
          <p>Nenhuma conta encontrada. Crie um título para acompanhar seus recebimentos.</p>
          <Button variant="outline" className="mt-3" onClick={() => { setSubmitError(null); setShowForm(true) }}>Novo Título</Button>
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/contas-receber')({
  component: ContasReceberPage,
})
